import { UserRoleEnum } from '~enums';

export const defaultWikiAllowedRoles: UserRoleEnum[] = [
	UserRoleEnum.SuperAdmin,
	UserRoleEnum.ClientAdmin,
	UserRoleEnum.Support,
];

const defaultWikiAllowedRolesSet = new Set(defaultWikiAllowedRoles);

export interface WikiManifestSectionNode {
	type: 'section';
	title: string;
	id: string;
	children: WikiManifestNode[];
}

export interface WikiManifestPageNode {
	type: 'page';
	title: string;
	file: string;
	id: string;
	allowedRoles?: string[];
}

export type WikiManifestNode = WikiManifestSectionNode | WikiManifestPageNode;

export interface WikiManifest {
	indexPath?: string;
	toc: WikiManifestNode[];
}

const resolvePageAllowedRoles = (page: WikiManifestPageNode): UserRoleEnum[] => {
	const allowedRoles = page.allowedRoles;
	if (!Array.isArray(allowedRoles)) {
		return defaultWikiAllowedRoles;
	}

	const normalized = allowedRoles.filter((role): role is UserRoleEnum =>
		defaultWikiAllowedRolesSet.has(role as UserRoleEnum)
	);
	return normalized;
};

export const isWikiPageAuthorized = (
	page: WikiManifestPageNode,
	userRole: UserRoleEnum | undefined
): boolean => {
	if (!userRole) {
		return false;
	}
	return resolvePageAllowedRoles(page).includes(userRole);
};

export const filterAuthorizedWikiNodes = (
	nodes: WikiManifestNode[],
	userRole: UserRoleEnum | undefined
): WikiManifestNode[] => {
	const filteredNodes: WikiManifestNode[] = [];

	for (const node of nodes) {
		if (node.type === 'page') {
			if (isWikiPageAuthorized(node, userRole)) {
				filteredNodes.push(node);
			}
			continue;
		}

		const filteredChildren = filterAuthorizedWikiNodes(node.children, userRole);
		if (filteredChildren.length > 0) {
			filteredNodes.push({ ...node, children: filteredChildren });
		}
	}

	return filteredNodes;
};

export const findWikiPageById = (
	nodes: WikiManifestNode[],
	pageId: string
): WikiManifestPageNode | undefined => {
	for (const node of nodes) {
		if (node.type === 'page') {
			if (node.id === pageId) {
				return node;
			}
			continue;
		}

		const found = findWikiPageById(node.children, pageId);
		if (found) {
			return found;
		}
	}
	return undefined;
};

export const findWikiPageByFile = (
	nodes: WikiManifestNode[],
	file: string
): WikiManifestPageNode | undefined => {
	for (const node of nodes) {
		if (node.type === 'page') {
			if (node.file === file) {
				return node;
			}
			continue;
		}

		const found = findWikiPageByFile(node.children, file);
		if (found) {
			return found;
		}
	}
	return undefined;
};
